import { useEffect, useRef } from 'react';
import gsap from 'gsap';

/**
 * 通用 GSAP 元素進場動畫（淡入 + 上浮 + Stagger 階梯交錯）
 */
export function useStaggerEntrance<T extends HTMLElement = HTMLDivElement>(
  selector: string,
  triggerKey: unknown,
  options?: {
    y?: number;
    duration?: number;
    stagger?: number;
    delay?: number;
  }
) {
  const containerRef = useRef<T>(null);
  const y = options?.y ?? 14;
  const duration = options?.duration ?? 0.45;
  const stagger = options?.stagger ?? 0.06;
  const delay = options?.delay ?? 0.05;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const targets = el.querySelectorAll(selector);
    if (!targets.length) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        targets,
        {
          opacity: 0,
          y,
        },
        {
          opacity: 1,
          y: 0,
          duration,
          stagger,
          delay,
          ease: 'power2.out',
          clearProps: 'transform,opacity',
        }
      );
    }, el);

    return () => ctx.revert();
  }, [selector, triggerKey, y, duration, stagger, delay]);

  return containerRef;
}

/**
 * 數值平滑滾動成長動畫（GSAP Ticker）
 */
export function animateNumber(
  node: HTMLElement,
  endValue: number,
  options?: {
    duration?: number;
    decimals?: number;
    prefix?: string;
    suffix?: string;
  }
) {
  const decimals = options?.decimals ?? 2;
  const obj = { val: 0 };
  return gsap.to(obj, {
    val: endValue,
    duration: options?.duration ?? 0.8,
    ease: 'power2.out',
    onUpdate: () => {
      const formatted = obj.val.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      node.textContent = `${options?.prefix ?? ''}${formatted}${options?.suffix ?? ''}`;
    },
  });
}

let pausesWhenHidden = false;

/**
 * 分頁切到背景時暫停整條 GSAP 全域時間軸，回到前景再繼續。
 * 常駐的循環動畫（吉祥物的呼吸、眨眼）在看不到的時候不該繼續吃 CPU。
 * 可以重複呼叫；只會掛一次監聽器（整個 SPA 生命週期都需要它，不拆）。
 */
export function pauseGsapWhenHidden(): void {
  if (pausesWhenHidden || typeof document === 'undefined') return;
  pausesWhenHidden = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') gsap.globalTimeline.pause();
    else gsap.globalTimeline.resume();
  });
}
